import { todayKey } from '../lib/datetime';
import { rememberWatched } from '../lib/postWatchAsk'
import { guestDaysLeft } from '../lib/guestUpgrade'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { mintKeepsake } from '../lib/keepsake'
import { writeActivity } from '../lib/activity'
import { endWatchSession } from '../lib/watchSessions'
import { getFriendsOnShow, hasFiredInSync, markFiredInSync } from './useFriendsWatching'
import { tmdbGet, tmdbEvict } from '../lib/tmdb'
import { pickStaleRows, SWEEP_CAP } from '../lib/staleSweep'

const SWEEP_AT_KEY = 'nextep_sweep_at'
const RESUME_SWEEP_MS = 6 * 60 * 60 * 1000
import { caughtUpFlip, endedAndFinished, isEndedStatus, deriveFinalEpisode, consumeManualStatusOverride, effectiveLastAired, isCaughtUp } from '../lib/episode'
import { safeStorage } from '../lib/safeStorage'
import { reportSaveError } from '../lib/saveError'
import { isNetworkError, enqueue, applyOutbox, replay, readOutbox, writeOutbox } from '../lib/watchlistOutbox'
import { readCachedRows, writeCachedRows } from '../lib/watchlistCache'
import { revivalInfo } from '../lib/revival'
import { logEvent } from '../lib/analytics'
import { streakFromDays } from '../lib/streakMath'
import { checkCanonCompletions } from '../lib/canonCeremony'
import { recordActivityToday } from '../lib/activityStreak'

// Same round-number set Cliffhanger uses for its own streak milestone cinematic
// (kept as a separate local copy, not shared, so the two streaks stay decoupled —
// a watch streak and a Cliffhanger streak are different numbers for different users).
// 50 and 100 are deliberately absent: lib/marquees.js owns those two as trophies
// (streak_50 / streak_100), and firing the flame cinematic too played two full-
// screen moments for one milestone (2026-10-11 rewards audit).
const WATCH_STREAK_MILESTONES = new Set([7, 14, 30, 75, 150, 200, 250, 300, 365])
const isWatchStreakMilestone = (n) => WATCH_STREAK_MILESTONES.has(n) || (n > 365 && n % 100 === 0)

export const STATUS_LABELS = {
  watching:      'Watching',
  caught_up:     'Caught Up',
  completed:     'Completed',
  rewatching:    'Rewatching',
  plan_to_watch: 'Plan to Watch',
  dropped:       'Abandoned',   // the status key stays `dropped`; the WORD is Abandoned
}
export const STATUS_COLORS = {
  watching:      'var(--green)',
  caught_up:     'var(--state-sync)',
  completed:     'var(--amber)',
  rewatching:    'var(--purple)',
  plan_to_watch: 'var(--blue)',
  dropped:       'var(--text-muted)',
}

// The one canonical status list — icon + description + the `abandon` routing flag,
// on top of STATUS_LABELS/STATUS_COLORS above. Previously the SET STATUS sheet on
// ShowDetail hard-coded its OWN copy of this (icons, a different label — "Abandon"
// vs STATUS_LABELS' "Abandoned" — and a different color, var(--state-warn) vs the
// canonical var(--text-muted)), so the persistent status pill and the sheet used to
// pick that same status could visibly disagree (audit 2026-08-15). Both now read
// from here.
export const STATUS_OPTIONS = [
  { key: 'watching',      icon: '▶',  desc: 'Making your way through' },
  { key: 'caught_up',     icon: '✨', desc: 'Seen all that has aired' },
  { key: 'completed',     icon: '✅', desc: 'Seen every episode' },
  { key: 'rewatching',    icon: '🔁', desc: 'Round two (or more)' },
  { key: 'plan_to_watch', icon: '🔖', desc: 'On your someday list' },
  { key: 'dropped',       icon: '💡', desc: 'Give up on it, and say why', abandon: true },
].map(o => ({ ...o, label: STATUS_LABELS[o.key], color: STATUS_COLORS[o.key] }))


// deriveFinalEpisode is now shared from lib/episode (used here + ShowDetail's finale gate).

export function useWatchlist(user, { onAuthRequired, shareActivity, onBinge } = {}) {
  // App passes these as inline closures, so they changed identity on every App render
  // and rebuilt every callback here (and the wl memo handed to ~35 components) each
  // time any App-level state flipped. Read them through a ref instead.
  const cbRef = useRef({})
  cbRef.current = { onAuthRequired, onBinge }
  const bingeCountRef = useRef(0)
  const bingeTimerRef = useRef(null)
  useEffect(() => () => clearTimeout(bingeTimerRef.current), [])
  const [items,   setItems]   = useState([])
  const [ratings, setRatings] = useState({}) // showId -> 1-5 reel rating
  const [notes,   setNotes]   = useState({}) // showId -> string
  const [statuses,setStatuses]= useState({}) // showId -> watching|completed|plan_to_watch|dropped
  const [progress,setProgress]= useState({}) // showId -> { season, episode }
  const [epNotes, setEpNotes] = useState({}) // showId -> { "1-3": "note text", ... }
  const [rewatchCount, setRewatchCount] = useState({})
  const [completedAt,  setCompletedAt]  = useState({})
  const [finaleRatings, setFinaleRatings] = useState({}) // showId -> 1-5 finale ("stuck the landing") rating
  const [ready,   setReady]   = useState(false)

  // Live mirrors of statuses/progress so the deferred boot sweep (runs ~2s after
  // load, off a load-time `rows` snapshot) can tell whether the user changed a row
  // in the meantime and skip clobbering it (audit 2026-07-22).
  const statusesRef = useRef(statuses)
  const progressRef = useRef(progress)
  useEffect(() => { statusesRef.current = statuses }, [statuses])
  useEffect(() => { progressRef.current = progress }, [progress])

  // Synchronously invalidate `ready` the instant the user identity changes —
  // BEFORE the load effect below runs (effects fire after paint). Without this,
  // `ready` stays true from the previous load (on cold boot, the guest load sets
  // ready=true with items=[]) while `items` is still that stale/empty set for one
  // commit after login. Home's `user && items.length<3 && ready` gate then paints
  // the "Welcome / Browse Shows →" empty-state hero for a single frame — the flash
  // Jason saw on load. setState-during-render re-renders in place (no extra paint),
  // so the stale frame never reaches the screen. Guarded by the ref → runs once
  // per user change, no render loop.
  const readyUserRef = useRef(user?.id)
  if (readyUserRef.current !== user?.id) {
    readyUserRef.current = user?.id
    if (ready) setReady(false)
  }

  useEffect(() => {
    setReady(false)
    if (user) loadFromSupabase(user.id)
    else       loadGuest()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // GuestOnboarding writes progress/status straight to Supabase (it renders before this
  // hook has anything to hand it) then does a plain SPA navigate('/') — same user.id, so
  // the effect above never reruns and Home read the watchlist snapshot from BEFORE the
  // position was set. Real repro (2026-09-28): picked "On S1·E3" for a show, Home's very
  // first card said "Start from the beginning"; only a hard reload showed E3. Anything
  // that writes outside this hook and is about to hand off to a page that reads it fires
  // this event first, and this refetches for real rather than trusting local guesses.
  useEffect(() => {
    if (!user?.id) return
    const onRefresh = () => loadFromSupabase(user.id)
    window.addEventListener('nextep:watchlist-refresh', onRefresh)
    return () => window.removeEventListener('nextep:watchlist-refresh', onRefresh)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Resume sweep. The boot sweep only runs on a cold load, but an installed PWA (Play
  // Store TWA especially) stays resident for days, so "boot only" left the cached next
  // episodes stale for exactly the people who use the app most (Jason 2026-09-26). When
  // the app comes back to the foreground after RESUME_SWEEP_MS, re-read the rows and run
  // the same sweep. Fresh rows, not state: statuses may have changed since boot.
  useEffect(() => {
    const uid = user?.id
    if (!uid) return
    const onVis = async () => {
      if (document.visibilityState !== 'visible') return
      const last = Number(safeStorage.get(SWEEP_AT_KEY) || 0)
      if (Date.now() - last < RESUME_SWEEP_MS) return
      safeStorage.set(SWEEP_AT_KEY, String(Date.now()))
      try {
        const { data } = await supabase.from('watchlist').select('show_id, show_data, status, progress').eq('user_id', uid)
        if (data?.length) sweepStaleSchedules(data, uid)
      } catch { /* never block the app on a sweep */ }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Every watchlist row patch funnels through here so progress, status and rating
  // writes fail the same way: a network failure parks the patch in the outbox (the
  // optimistic state stands and a calm toast says so), a refusal reports as before.
  // Resolves 'ok' | 'queued' | 'failed'; callers roll back only on 'failed'.
  // The device snapshot lives in memory and reaches IndexedDB only at idle. A
  // per-write read-modify-write of the whole list (a few MB structured-cloned twice
  // on the main thread for a big library) landed right between cards on phones.
  const rowsRef = useRef(null)
  const cacheTimerRef = useRef(null)
  const scheduleCacheWrite = (uid) => {
    if (!uid || !rowsRef.current) return
    if (cacheTimerRef.current) return
    const run = () => { cacheTimerRef.current = null; if (rowsRef.current) writeCachedRows(uid, rowsRef.current) }
    cacheTimerRef.current = typeof window !== 'undefined' && window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 4000 })
      : setTimeout(run, 1500)
  }
  const patchRows = (uid, showId, patch) => {
    if (!rowsRef.current) return
    const id = Number(showId)
    rowsRef.current = rowsRef.current.map(r => (Number(r.show_id) === id ? { ...r, ...patch } : r))
    scheduleCacheWrite(uid)
  }

  const persistRow = async (uid, showId, patch, action) => {
    let error
    try {
      const r = await supabase.from('watchlist').update(patch).eq('user_id', uid).eq('show_id', showId).select('show_id')
      error = r?.error
      // Zero rows is not success: the row was removed on another device (or RLS
      // filtered it), and a mark that matched nothing must not stand on screen.
      if (!error && Array.isArray(r?.data) && r.data.length === 0) error = { code: 'NX_NO_ROW', message: 'No matching row' }
    } catch (e) { error = e }
    if (!error) { patchRows(uid, showId, patch); return 'ok' }
    if (isNetworkError(error)) {
      writeOutbox(safeStorage, uid, enqueue(readOutbox(safeStorage, uid), { showId, patch }))
      patchRows(uid, showId, patch)
      try { window.dispatchEvent(new CustomEvent('nextep:save-queued', { detail: { action } })) } catch {}
      return 'queued'
    }
    reportSaveError(error, action)
    return 'failed'
  }

  const flushingRef = useRef(false)
  const flushOutbox = async (uid) => {
    if (!uid || flushingRef.current) return
    const pending = readOutbox(safeStorage, uid)
    if (!pending.length) return
    flushingRef.current = true
    try {
      const left = await replay(pending,
        (e) => {
          let q = supabase.from('watchlist').update(e.patch).eq('user_id', uid).eq('show_id', e.showId)
          // A positional patch (progress) must not regress a newer position set on
          // another device while this one was offline: only land on rows not touched
          // since the mark was made. Zero rows then means "superseded", not failure.
          if (e.patch?.progress) q = q.or(`updated_at.is.null,updated_at.lt.${new Date(e.ts || 0).toISOString()}`)
          return q
        },
        { onRefused: (_e, err) => reportSaveError(err, 'sync') })
      writeOutbox(safeStorage, uid, left)
      const synced = pending.length - left.length
      if (synced) try { window.dispatchEvent(new CustomEvent('nextep:outbox-synced', { detail: { count: synced } })) } catch {}
    } finally { flushingRef.current = false }
  }

  // Replay whatever is owed the moment a connection comes back or the app resumes.
  useEffect(() => {
    const uid = user?.id
    if (!uid) return
    const flush = () => { flushOutbox(uid) }
    const onVis = () => { if (document.visibilityState === 'visible') flush() }
    window.addEventListener('online', flush)
    document.addEventListener('visibilitychange', onVis)
    return () => { window.removeEventListener('online', flush); document.removeEventListener('visibilitychange', onVis) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const hydratedRef = useRef(null)
  const loadFromSupabase = async (uid, attempt = 0) => {
    // Show the device's last snapshot first (with any unsynced patches laid over it) so
    // a boot with no signal is the real list, not an empty app; the fetch below then
    // replaces it with server truth.
    if (attempt === 0 && hydratedRef.current !== uid) {
      hydratedRef.current = uid
      const cached = await readCachedRows(uid)
      if (cached?.rows?.length) { rowsRef.current = cached.rows; applyRows(applyOutbox(cached.rows, readOutbox(safeStorage, uid))); setReady(true) }
    }
    // Owed writes go first so the read that follows returns what the user actually did.
    await flushOutbox(uid)
    const { data } = await supabase
      .from('watchlist')
      .select('show_id, show_data, rating, notes, status, progress, episode_notes, rewatch_count, completed_at, finale_rating')
      .eq('user_id', uid)
      .order('added_at', { ascending: false })
    // No rows came back (network/RLS hiccup on a cold boot). Flipping `ready` true with an
    // empty list would let the new-user import hero flash for an established user, so retry
    // once before giving up. bootWatchdog + the wl.ready gates keep this from hanging.
    // A cached snapshot, if one was shown above, stays put.
    if (!data) {
      if (attempt < 1) { setTimeout(() => loadFromSupabase(uid, attempt + 1), 900); return }
      setReady(true)
      return
    }
    applyRows(applyOutbox(data, readOutbox(safeStorage, uid)))
    setReady(true)
    rowsRef.current = data
    scheduleCacheWrite(uid)
    // Freshening cached "next episode" data is a background nicety, not urgent — and it
    // fires up to ~15 throttled TMDB calls over ~2s. Defer to idle so it doesn't drip on
    // the network/CPU during the exact window the user is first interacting.
    // Boot pass is capped at 8 (resume keeps the full cap): fewer TMDB GETs in the first seconds.
    const sweep = () => { safeStorage.set(SWEEP_AT_KEY, String(Date.now())); sweepStaleSchedules(data, uid, 8) }
    ;(window.requestIdleCallback || ((cb) => setTimeout(cb, 2500)))(sweep)
  }

  const applyRows = (data) => {
    {
      setItems(data.map(r => r.show_data).filter(Boolean))
      const rwc = {}, cat = {}, frt = {}
      data.forEach(r => {
        if (r.rewatch_count) rwc[r.show_id] = r.rewatch_count
        if (r.completed_at)  cat[r.show_id]  = r.completed_at
        if (r.finale_rating) frt[r.show_id]  = r.finale_rating
      })
      setRewatchCount(rwc)
      setCompletedAt(cat)
      setFinaleRatings(frt)
      // Note: we'll set epNotes after the forEach
      const r={}, n={}, s={}, p={}, en={}
      data.forEach(d => {
        if (d.rating)    r[d.show_id] = d.rating
        if (d.notes)     n[d.show_id] = d.notes
        if (d.status)    s[d.show_id] = d.status
        if (d.progress)  p[d.show_id] = d.progress
        if (d.episode_notes && Object.keys(d.episode_notes).length > 0) en[d.show_id] = d.episode_notes
      })
      setRatings(r); setNotes(n); setStatuses(s); setProgress(p); setEpNotes(en)
    }
  }

  // Background: TMDB next_episode_to_air is cached in show_data and can go stale,
  // since it is only refreshed when a show detail page is opened. On load, quietly
  // re-pull the watching/rewatching shows whose cached next episode is missing or
  // not strictly in the future, so the schedule never shows a stale or wrong-day air.
  const sweepStaleSchedules = async (rows, uid, cap = SWEEP_CAP) => {
    try {
      // Repair pass (local, no network): completed rows with NO progress at all —
      // the finale jump in setStatus is skipped when the show object isn't
      // available at completion time (e.g. old imports). Derive it from the
      // cached show_data now. Deliberately does NOT touch completed rows whose
      // progress is merely behind the latest aired ep — that gap is meaningful
      // (it's how THE RETURN revival detection knows new episodes exist).
      // Single batched setProgress: a per-row setState here re-rendered every
      // watchlist consumer (the whole app) once per repaired row at boot.
      const progressFixes = {}
      rows.forEach(r => {
        if (r.status !== 'completed' || r.progress?.season || !r.show_data) return
        const fin = deriveFinalEpisode(r.show_data)
        if (!fin) return
        progressFixes[r.show_id] = fin
        supabase.from('watchlist').update({ progress: fin })
          .eq('user_id', uid).eq('show_id', r.show_id).then(() => {}, () => {})
      })
      if (Object.keys(progressFixes).length) {
        setProgress(prev => ({ ...prev, ...progressFixes }))
      }
      const today = todayKey()
      // Which rows, and in what order: see lib/staleSweep. caught_up rows MUST be
      // refreshed (else a show never learns its new season aired and stays "Caught Up"
      // forever), and completed rows too (Jason 2026-09-19: MobLand S2 aired with no
      // notice; this is the ONLY path that ever refreshes a finished show's show_data).
      // But a completed row with no cached next episode ALWAYS reads as stale, and on a
      // mature list dozens of them used to fill the cap in list order every boot, so the
      // few shows actually airing this week never got refreshed (Jason 2026-09-26:
      // Deadliest Catch missing from Tonight / Upcoming for a week). Airing shows now go
      // first; finished shows rotate by day.
      const stale = pickStaleRows(rows, today, Math.floor(Date.now() / 86400000), cap)
      // Batched setItems: with up to 15 stale shows refreshed ~120ms apart, the
      // old per-show setItems was an app-wide re-render drip for ~2s at boot
      // (same bug class as the useAirSchedule per-resolve setState). Collect
      // merged rows and apply ONCE; the DB writes stay per-show fire-and-forget.
      const mergedById = {}
      for (const r of stale) {
        try {
          // Evict first: /tv/{id} details sit in a no-TTL session cache (tmdb.js
          // tvCache), so a resume sweep in a long-resident app would otherwise read
          // back the very snapshot it is trying to refresh. The fresh fetch then
          // re-populates the caches for the show page and everyone else.
          tmdbEvict('/tv/' + r.show_id)
          const fresh = await tmdbGet('/tv/' + r.show_id)
          if (!fresh || !fresh.id) continue
          const old = r.show_data || {}
          const fn1 = fresh.next_episode_to_air || {}
          const on1 = old.next_episode_to_air || {}
          const fl1 = fresh.last_episode_to_air || {}
          const ol1 = old.last_episode_to_air || {}
          const changed = (fn1.air_date !== on1.air_date) || (fl1.episode_number !== ol1.episode_number) || (fresh.status !== old.status)
          if (!changed) continue
          const merged = { ...old, ...fresh }
          mergedById[r.show_id] = merged
          supabase.from('watchlist').update({ show_data: merged }).eq('user_id', uid).eq('show_id', r.show_id).then(() => {}, () => {})
          // The Return, noticed here: a finished show whose fresh data shows a new
          // season is told once, on Home, instead of waiting to be found on its own
          // page (Gold Rush S17 aired 2026-10-02 and Jason never heard, because the
          // only surface was the show page he had no reason to open).
          if (r.status === 'completed') {
            const info = revivalInfo(merged, 'completed', r.completed_at, r.progress)
            const key = `nextep_revival_noticed_${r.show_id}_s${info?.season}`
            if (info && !safeStorage.get(key)) {
              try { safeStorage.set(key, '1') } catch {}
              window.dispatchEvent(new CustomEvent('nextep:milestone', { detail: {
                title: `${merged.name} is back`,
                body: info.upcoming && !info.airDate ? `Season ${info.season} is coming.` : `Season ${info.season} has started. Tap to pick it back up.`,
                icon: '🎬', route: `/show/${r.show_id}`,
              }}))
            }
          }
        } catch (e) { /* skip this show */ }
        await new Promise(res => setTimeout(res, 120))
      }
      if (Object.keys(mergedById).length) {
        if (rowsRef.current) {
          rowsRef.current = rowsRef.current.map(r => (mergedById[r.show_id] ? { ...r, show_data: mergedById[r.show_id] } : r))
          scheduleCacheWrite(uid)
        }
        setItems(prev => prev.map(i => (i && mergedById[i.id]) ? mergedById[i.id] : i))
      }

      // BIDIRECTIONAL caught_up reconcile via the ONE shared decision (caughtUpFlip):
      // watching→caught_up when you've caught up off a quick-mark surface (Home/MyList/
      // Calendar) and caught_up→watching once a new episode actually airs. The old sweep
      // only did the first direction, so a show stayed stuck "Caught Up" after its next
      // season dropped (you'd have to open the show to un-stick it). Uses the freshest
      // show_data (refreshed above, which now includes caught_up rows) + today, and is the
      // SAME predicate the display + ShowDetail flips use, so nothing oscillates.
      const statusFixes = []   // { sid, from, to }
      for (const r of rows) {
        const sd = mergedById[r.show_id] || r.show_data
        // A manual status-sheet pick or a RevivalBanner Resume tap can leave a row sitting
        // at exactly the air-data condition this sweep exists to correct (progress ahead of
        // what's aired) — this boot sweep runs on every cold launch, independent of whether
        // ShowDetail's own reconcile effect ever mounts again, so it needs the SAME guard or
        // relaunching the app (not just reopening the show) would silently undo the choice
        // (2026-08-02 audit, after the ShowDetail-only fix left this path uncovered).
        if (consumeManualStatusOverride(safeStorage, r.show_id, r.status, r.progress)) continue
        const target = caughtUpFlip(sd, r.progress, r.status, today)
        if (target && target !== r.status) statusFixes.push({ sid: r.show_id, from: r.status, to: target })
      }
      const fixedIds = new Set(statusFixes.map(f => f.sid))
      if (statusFixes.length) {
        // Guard each write against the load-time status (`.eq('status', from)`) so a row
        // the user changed during the sweep window is skipped by the DB rather than
        // clobbered; mirror the guard on the optimistic local state (audit 2026-07-22).
        statusFixes.forEach(({ sid, from, to }) => supabase.from('watchlist').update({ status: to })
          .eq('user_id', uid).eq('show_id', +sid).eq('status', from).then(() => {}, () => {}))
        setStatuses(prev => {
          const n = { ...prev }
          statusFixes.forEach(({ sid, from, to }) => { if ((prev[sid] ?? from) === from) n[sid] = to })
          return n
        })
      }

      // Air-data-driven COMPLETION: caughtUpFlip (above) can't promote an ended row —
      // so a show you were caught up on that TMDB later flips to Ended (its finale being
      // the last ep you already watched) stays stuck 'caught_up' forever. Quietly complete
      // those here via the normal completion path so the Cinephile Card mints and
      // completed_at stamps. opts.bulk keeps it silent: NO Final Cut / broadcast / milestone
      // for a show finished weeks ago. Guarded so it never re-fires (a 'completed' row fails
      // endedAndFinished's status check next boot) and only acts on positive air data.
      for (const r of rows) {
        if (fixedIds.has(r.show_id)) continue
        const sd = mergedById[r.show_id] || r.show_data
        // Same manual-pick guard as the caughtUpFlip loop above (consume keeps the
        // key on a match, so checking twice is safe): a user who deliberately moved
        // an ended, fully-watched show back to watching would otherwise be quietly
        // re-completed here on every cold launch, with a fresh completed_at stamped.
        if (consumeManualStatusOverride(safeStorage, r.show_id, r.status, r.progress)) continue
        if (!endedAndFinished(sd, r.progress, r.status, today)) continue
        // Don't auto-complete a row the user moved since the load-time snapshot:
        // skip if its live status OR progress no longer matches what the decision was
        // based on (setStatus stamps completed_at + mints a card, so a stale write here
        // is real corruption — the un-mark-below-finale race, audit 2026-07-22).
        if (statusesRef.current[r.show_id] !== r.status) continue
        const liveP = progressRef.current[r.show_id]
        const snapP = r.progress
        if (liveP && ((liveP.season || 0) !== (snapP?.season || 0) || (liveP.episode || 0) !== (snapP?.episode || 0))) continue
        setStatus(r.show_id, 'completed', { bulk: true, show: sd })
      }
    } catch (e) { /* never block the app on a sweep */ }
  }

  const loadGuest = async () => {
    const ids = (() => { try { return JSON.parse(localStorage.getItem('nextep_guest')||'[]') } catch { return [] } })()
    if (!ids.length) { setItems([]); setReady(true); return }
    const res = await Promise.allSettled(ids.map(id => tmdbGet(`/tv/${id}`)))
    setItems(res.filter(r=>r.status==='fulfilled').map(r=>r.value))
    setReady(true)
  }

  const add = useCallback(async (show) => {
    if (user && leaseUp()) return
    // The show rides along so App can take the guest door (lib/guestAdd) instead of
    // a sign-in wall; every other caller still gets the bare callback.
    if (!user) { cbRef.current.onAuthRequired?.(show); return false }
    if (items.some(i => i.id === show.id)) return false
    // Preserve any existing rating/notes/progress if the show was previously in the list
    const existingRating   = ratings[show.id] || null
    const existingNotes    = notes[show.id]   || null
    const existingProgress = progress[show.id]|| null
    const isFirstShow = items.length === 0
    // OPTIMISTIC: the card appears the instant you tap Add — the write runs in the
    // background below and rolls back only if it fails. (add() still AWAITS the write for
    // its boolean return, which the import loop relies on — instant UI, honest result.)
    setItems(prev => [show, ...prev])
    setStatuses(prev => ({ ...prev, [show.id]: 'plan_to_watch' }))
    const { error } = await supabase.from('watchlist').upsert({
      user_id: user.id, show_id: show.id, show_data: show,
      status: 'plan_to_watch',
      rating: existingRating,
      notes:  existingNotes,
      progress: existingProgress,
    })
    // Search and Discover hand over a /search/tv stub (no seasons, status or episode
    // markers). Fill it in right away so the row is never blind to its own season.
    if (!error && !Array.isArray(show.seasons)) {
      tmdbGet('/tv/' + show.id).then(fresh => {
        if (!fresh?.id) return
        const merged = { ...show, ...fresh }
        setItems(prev => prev.map(i => (i?.id === show.id ? merged : i)))
        supabase.from('watchlist').update({ show_data: merged }).eq('user_id', user.id).eq('show_id', show.id).then(() => {}, () => {})
      }).catch(() => {})
    }
    if (error) {
      // Roll back the optimistic add — the write didn't stick.
      setItems(prev => prev.filter(i => i.id !== show.id))
      setStatuses(prev => { const n = { ...prev }; delete n[show.id]; return n })
      reportSaveError(error, 'add to list')
      return false
    }
    // Celebration + activation analytics fire only once the write actually landed, so a
    // failed add never pops a spotlight or logs a phantom activation.
    window.dispatchEvent(new CustomEvent('nextep:show_added', { detail: { ...show, isFirstShow } }))
    // `src` = the route the add happened on (2026-09-12): Discover vs a show page vs
    // Home vs import vs onboarding. 14 adds in 5 days and no way to tell which door
    // they came through — the one thing the retention work needs to know.
    logEvent('show_added', { first: isFirstShow, src: (typeof location !== 'undefined' ? location.pathname : '').replace(/^(\/show|\/aftershow|\/canon|\/curator|\/u)\/.*$/, '$1/*') })
    if (isFirstShow) {
      try { localStorage.setItem('nextep_first_show_celebrated', '1') } catch {}
    }
    // Funnel: the ACTIVATION moment — this device just tracked its first-ever show
    // (by any path: manual add, import, or spotlight). Guarded by a durable flag so it
    // fires exactly once per device, even across sessions and even if they later clear
    // and re-add. This is the number that actually matters: booted → activated.
    try {
      if (!localStorage.getItem('nextep_first_show_logged')) {
        localStorage.setItem('nextep_first_show_logged', '1')
        logEvent('first_show_added')
      }
    } catch { /* storage blocked, skip the once-guard rather than risk a dupe every add */ }
    return true
  }, [user, items, ratings, notes, progress])

  const remove = useCallback(async (showId) => {
    if (!user) { cbRef.current.onAuthRequired?.(); return }
    // OPTIMISTIC: the card disappears the instant you tap; re-inserts if the delete fails.
    // (Rating/notes/progress are intentionally kept — restored if the user re-adds.)
    let removedItem, removedStatus
    setItems(prev => { removedItem = prev.find(i => i.id === showId); return prev.filter(i => i.id !== showId) })
    setStatuses(prev => { removedStatus = prev[showId]; const n = { ...prev }; delete n[showId]; return n })
    const { error } = await supabase.from('watchlist').delete().eq('user_id', user.id).eq('show_id', showId)
    if (error) {
      // Roll back — nothing was deleted.
      if (removedItem) setItems(prev => prev.some(i => i.id === showId) ? prev : [removedItem, ...prev])
      if (removedStatus) setStatuses(prev => ({ ...prev, [showId]: removedStatus }))
      reportSaveError(error, 'remove')
      return
    }
    // Clear any watch plans for the removed show so they don't linger as
    // orphans in Tonight's Lineup (mirrors the completed/dropped cleanup).
    supabase.from('watch_plans').delete().eq('user_id', user.id).eq('show_id', showId)
      .then(({ error: e }) => { if (e) console.warn('watch_plans cleanup on remove:', e.message) })
  }, [user])

  const rate = useCallback(async (showId, stars, opts = {}) => {
    // bulk mode (used by the importer): write the rating quietly — no follower
    // broadcast, no 5-Reel milestone, no toast. Importing a rated CSV otherwise
    // spammed the feed with dozens of "rated" activities + celebration modals
    // (audit 2026-07-08). Mirrors setStatus({ bulk: true }).
    if (!user) { cbRef.current.onAuthRequired?.(); return }
    // Capture the prior rating BEFORE the write so we can tell a new 5-reel crossing
    // from a re-commit of an already-5 show (re-tapping 5 shouldn't re-pop the honor).
    const prevRating = ratings[showId] ?? ratings[+showId]
    // OPTIMISTIC: the star lights the instant you tap it; rolls back only if the write fails.
    setRatings(prev => stars ? { ...prev, [showId]: stars } : (({ [showId]: _, ...rest }) => rest)(prev))
    const res = await persistRow(user.id, showId, { rating: stars || null }, 'rating')
    if (res === 'failed') {
      // Roll the star back to its prior value — the write didn't persist.
      setRatings(prev => prevRating ? { ...prev, [showId]: prevRating } : (({ [showId]: _, ...rest }) => rest)(prev))
      return
    }
    const show = items.find(i => i.id === showId || i.id === +showId)
    // Only celebrate a NEW 5-reel rating (crossed to 5, not a re-commit), post-write.
    if (!opts.bulk && stars === 5 && prevRating !== 5 && show) {
      window.dispatchEvent(new CustomEvent('nextep:milestone', { detail: {
        title: '5-Reel Honor',
        body:  show.name,
        icon:  '⭐',
        route: `/show/${show.id}`,
      }}))
    }
    if (stars && !opts.bulk) writeActivity(user.id, shareActivity, 'rated', show, { rating: stars })
    // Brief confirmation for 1-4 star ratings (5-star already gets milestone modal)
    if (!opts.bulk && stars && stars < 5 && show) {
      window.dispatchEvent(new CustomEvent('nextep:rating-saved', { detail: { show, stars } }))
    }
    // Emotional trigger: a 4.5–5 reel score is the peak moment to invite a review
    // ("you clearly loved it — say why"). Reviews are drastically underused, so we
    // ask only at the top of the scale. The global ReviewInvite decides whether to
    // actually show (per-show, dismissible, once).
    if (!opts.bulk && stars >= 4.5 && show) {
      window.dispatchEvent(new CustomEvent('nextep:review-invite', { detail: { show, stars } }))
    }
  }, [user, items, ratings, shareActivity])

  // A separate "did it stick the landing?" rating for JUST the finale, parallel to
  // the overall `rate` — same 0.5-step reel scale, its own column. No milestone/
  // activity noise; it's a quiet, opt-in verdict on the ending.
  const rateFinale = useCallback(async (showId, n) => {
    if (!user) { cbRef.current.onAuthRequired?.(); return }
    const { error } = await supabase.from('watchlist').update({ finale_rating: n || null }).eq('user_id', user.id).eq('show_id', showId)
    if (reportSaveError(error, 'finale rating')) return   // skip optimistic UI if it didn't persist
    setFinaleRatings(prev => n ? { ...prev, [showId]: n } : (({ [showId]: _, ...rest }) => rest)(prev))
  }, [user])

  const saveEpNote = useCallback(async (showId, season, episode, text) => {
    if (!user) { cbRef.current.onAuthRequired?.(); return false }
    const key      = `${season}-${episode}`
    const existing = epNotes[showId] || {}
    const updated  = text?.trim() ? { ...existing, [key]: text.trim() } : (() => { const { [key]:_, ...rest } = existing; return rest })()
    setEpNotes({ ...epNotes, [showId]: updated })
    const { error } = await supabase.from('watchlist').update({ episode_notes: updated }).eq('user_id', user.id).eq('show_id', showId)
    reportSaveError(error, 'episode note')
    return !error
  }, [user, epNotes])

  const saveNote = useCallback(async (showId, text) => {
    if (!user) { cbRef.current.onAuthRequired?.(); return }
    {
      const { error } = await supabase.from('watchlist').update({ notes: text || null }).eq('user_id', user.id).eq('show_id', showId)
      if (reportSaveError(error, 'note')) return   // skip optimistic UI if it didn't persist
    }
    setNotes(prev => text ? { ...prev, [showId]: text } : (({ [showId]: _, ...rest }) => rest)(prev))
  }, [user])

  // opts.bulk  — quiet mode for batch operations (history import): records the
  //              status fully (completed_at, progress jump, card minted into the
  //              binder) but fires NO activity broadcast, NO FinalCut, NO card
  //              reveal modal, NO milestone — importing 50 shows must not spam
  //              followers with pushes or queue 50 cinematics.
  // opts.show  — explicit show object for when the caller just add()ed it and
  //              the items state hasn't propagated to this closure yet.
  const setStatus = useCallback(async (showId, status, opts = {}) => {
    if (!user) { cbRef.current.onAuthRequired?.(); return }
    // Re-picking the already-active status is a NO-OP. Without this, tapping the
    // active option in any status sheet re-ran the whole branch: "Rewatching"
    // double-incremented rewatch_count and reset a mid-rewatch to S1E1; "Done"
    // re-stamped completed_at (destroying the original date the Vault/anniversaries
    // read), re-posted a duplicate completed activity, and re-fired Final Cut.
    // opts.completedAt (import supplying a real historical date) still passes.
    // Re-picking "Done" on a show that is already completed but has aired new
    // episodes since (a revival on the deck) means "I'm done with this show, new
    // season included": re-stamp the finish and move the marker to the finale, so
    // it leaves the deck. A plain re-pick with nothing new stays a no-op.
    const curShow = items.find(i => i?.id === showId || i?.id === +showId) || opts.show || null
    const reComplete = status === 'completed' && statuses[showId] === 'completed'
      && (!completedAt[showId] || !!revivalInfo(curShow, 'completed', completedAt[showId], progress[showId]))
    if (statuses[showId] === status && !opts.completedAt && !reComplete) return
    // OPTIMISTIC: the status chip flips the instant you tap; capture the prior value in the
    // updater (so we don't need `statuses` in deps) to roll back if the write fails.
    let prevStatus
    setStatuses(prev => { prevStatus = prev[showId]; return { ...prev, [showId]: status } })
    {
      // Stamp updated_at so this counts as watch activity for the daily streak, which is
      // computed purely from watchlist.updated_at. There is no DB trigger, so without this
      // an explicit set the row's timestamp never moves and the streak can't see the action.
      const res = await persistRow(user.id, showId, { status, updated_at: new Date().toISOString() }, 'status change')
      if (res === 'failed') {
        // Roll the chip back and skip the completion side effects — nothing persisted.
        setStatuses(prev => prevStatus ? { ...prev, [showId]: prevStatus } : (({ [showId]: _, ...rest }) => rest)(prev))
        return
      }
    }
    // `show` lookup is best-effort — when this is called immediately after wl.add(),
    // the React state update for `items` may not have propagated to this closure yet,
    // so we fall back to the caller-supplied opts.show (import flow), then to null.
    const show = items.find(i => i?.id === showId || i?.id === +showId) || opts.show || null
    if ((status === 'watching' || status === 'completed') && !opts.bulk && !reComplete) {
      writeActivity(user.id, shareActivity, status, show, {})
    }
    if (status === 'completed' || status === 'dropped') endWatchSession(user.id, showId)
    // Track completion date and rewatch count
    const extra = {}
    // Leaving `completed` must clear the completion artifacts, or they haunt the
    // row: a stale completed_at keeps firing Calendar anniversaries for a show the
    // user un-completed, and completed→plan_to_watch otherwise shows a someday-list
    // row wearing a ~100% progress bar (the finale-jump written on completion).
    // Progress is only cleared for plan_to_watch — completed→watching keeps it, so
    // a revival resumes from the finale (next-to-watch = first new episode).
    // A rewatch is not an un-finish: the original completed_at survives it (the Vault,
    // anniversaries and the Final Cut card all read that date).
    if (prevStatus === 'completed' && status !== 'completed' && status !== 'rewatching') {
      extra.completed_at = null
      setCompletedAt(prev => (({ [showId]: _, ...rest }) => rest)(prev))
      if (status === 'plan_to_watch') {
        extra.progress = null
        setProgress(prev => (({ [showId]: _, ...rest }) => rest)(prev))
      }
    }
    if (status === 'completed') {
      // opts.completedAt lets an IMPORT supply the real historical watch date from the
      // source file. Without it every imported show reads as finished at import time,
      // which flattens the whole library onto one day and empties the Rewatch Vault.
      // Already validated by parseWatchedDate; re-checked here so no caller can write junk.
      const supplied = opts.completedAt && Number.isFinite(Date.parse(opts.completedAt))
        ? new Date(opts.completedAt).toISOString()
        : null
      // Ending a rewatch keeps the ORIGINAL finish date; only a first completion stamps now.
      extra.completed_at = supplied || (prevStatus === 'rewatching' && completedAt[showId]) || new Date().toISOString()
      setCompletedAt(prev => ({ ...prev, [showId]: extra.completed_at }))
      // Advance progress to the final aired episode so all episodes count as watched
      // (the watched-state UI uses progress.season + episode to decide which rows/chips
      // get the green treatment, so landing on the finale marks everything before it).
      // Only attempt this if we actually have the show object available — when called
      // immediately after wl.add() the show may not be in items yet; in that case the
      // progress jump is skipped and the user can still mark progress manually if they care.
      if (show) {
        const finalEp = deriveFinalEpisode(show)
        if (finalEp) {
          extra.progress = finalEp
          setProgress(prev => ({ ...prev, [showId]: finalEp }))
        }
        // The Final Cut: finishing a confirmed-ended series earns the cinematic no
        // matter which path marked it complete (status toggle, finale crossing, etc).
        // Centralized here so every completion route fires it consistently.
        const tmdbStatus = (show.status || '').toLowerCase()
        const isEndedSeries = tmdbStatus === 'ended' || tmdbStatus === 'canceled' || tmdbStatus === 'cancelled'
        if (isEndedSeries && finalEp && !opts.bulk && !reComplete) {
          window.dispatchEvent(new CustomEvent('nextep:finalcut', { detail: { show, rating: (ratings && ratings[showId]) || 0, user } }))
        }
        // The Canon, sealed: did THIS completion just tip a whole franchise from
        // <100% to 100%? Fire-and-forget, best-effort — never blocks the completion
        // itself. Fully separate from FinalCut's chain (a show can trigger both).
        if (finalEp && !opts.bulk) {
          checkCanonCompletions({
            user, show, showId, finalEp, prevStatus, prevProgress: progress[showId],
            wl: { statuses, progress },
            onFire: (detail) => window.dispatchEvent(new CustomEvent('nextep:canonseal', { detail })),
          }).catch(() => {})
        }
      }
      // Auto-resolve any active watch challenges for this show. A freshly-imported
      // show has no pending challenges, so skip this SELECT (+ its follow-up writes)
      // entirely in bulk mode — it's pure round-trip waste at import scale.
      if (!opts.bulk) {
        supabase.from('challenges')
          .select('id, challenger, challenged, status')
          .eq('show_id', showId)
          .in('status', ['pending', 'accepted'])
          .then(({ data: chals }) => {
            if (!chals?.length) return
            const now = new Date().toISOString()
            chals.forEach(ch => {
              const field = ch.challenger === user.id ? 'challenger_completed_at' : 'challenged_completed_at'
              supabase.from('challenges').update({ [field]: now }).eq('id', ch.id).catch(() => {})
            })
          }).catch(() => {})
      }

      // The Final Cut card (2026-09-20): one card per finished show, minted here so
      // every completion path — status sheet, finale crossing, import — leaves one
      // behind. Idempotent on (user, show): a re-completion keeps the original date
      // and mints nothing new. The reveal (KeepsakeReveal, CinematicLayer) only
      // fires for a NEW card and waits behind the Final Cut cinematic if one plays.
      if (show) {
        mintKeepsake({ user, show, rating: (ratings && ratings[showId]) || null, completedAt: extra.completed_at, bulk: !!opts.bulk })
          .then(card => { if (card && !opts.bulk) window.dispatchEvent(new CustomEvent('nextep:keepsake-minted', { detail: card })) })
          .catch(() => {})
      }
      // The 16.x Cinephile Card mint used to run here too (into cinephile_cards,
      // "quietly, for a future return"). Stopped 2026-09-20: the Final Cut card
      // above IS the return, and this was a write on every completion for a
      // surface that no longer exists. The table and lib/cinephileCards.js stay.
      // Completion milestones (10/25/50/100) are now Marquees — the MarqueeEngine
      // detects the completed-count crossing and fires the trophy cinematic. The
      // old nextep:milestone-completion modal has been retired into that one
      // system (Jason's "re-purpose, don't duplicate" call, 2026-06-19).
    }
    if (status === 'rewatching') {
      const newCount = (rewatchCount[showId] || 0) + 1
      // Default to S1E1, but honor an explicit start point so a rewatch can begin
      // mid-series (opts.rewatchFrom = { season, episode }) instead of always resetting.
      const startAt = opts.rewatchFrom && opts.rewatchFrom.season ? opts.rewatchFrom : { season: 1, episode: 1 }
      extra.rewatch_count = newCount
      extra.progress = startAt
      setRewatchCount(prev => ({ ...prev, [showId]: newCount }))
      setProgress(prev => ({ ...prev, [showId]: startAt }))
      // Starting a rewatch used to just close the sheet silently — RewatchSheet
      // set the status and the modal disappeared with zero acknowledgement that
      // anything happened. This mints the "Rewatch #N" moment (RewatchStart.jsx,
      // mounted in CinematicLayer) with the REAL show + count, not a placeholder.
      // Skip during bulk import — an import has its own finale screen.
      if (show && !opts.bulk) {
        window.dispatchEvent(new CustomEvent('nextep:rewatchstart', { detail: { show, count: newCount, startAt } }))
      }
      // A rewatch starts a clean slate: any still-pending plans from the PREVIOUS
      // pass (a Run whose episodes got watched without confirming each plan) are
      // moot, and the nightly roll-forward would otherwise keep dragging them onto
      // Tonight forever. Jason 2026-09-05: restarting Punisher at S2·E1 flooded the
      // lineup with 11 stale S2 plans from an August run. Mark them skipped (kept
      // as history, never deleted), don't leave them pending.
      if (!opts.bulk) {
        supabase.from('watch_plans')
          .update({ status: 'skipped' })
          .eq('user_id', user.id).eq('show_id', showId).eq('status', 'pending')
          .then(({ error }) => { if (error) console.warn('watch_plans rewatch reset:', error.message) })
      }
    }

    // Completed/dropped shows shouldn't leave lingering watch_plans on
    // Tonight's Lineup, calendar, etc. Wipe them when the show exits
    // active rotation. Fire-and-forget — failures don't block the status change.
    // A freshly-imported show has no watch_plans, so skip this DELETE in bulk mode.
    if ((status === 'completed' || status === 'dropped') && !opts.bulk) {
      supabase.from('watch_plans')
        .delete()
        .eq('user_id', user.id)
        .eq('show_id', showId)
        .then(({ error }) => { if (error) console.warn('watch_plans cleanup:', error.message) })
    }
    if (Object.keys(extra).length) persistRow(user.id, showId, extra, 'progress')
    // ratings + rewatchCount are read above (FinalCut/card-mint use ratings[showId];
    // rewatch_count derives from rewatchCount[showId]) — they MUST be deps or the
    // callback captures a stale snapshot (rate-then-complete mints the wrong rating;
    // a second rewatch reuses the old count).
    // statuses + progress are ALSO deps now: checkCanonCompletions above reads
    // them fresh (both to reconstruct this show's pre-change state and to hold
    // every OTHER canon show's current state steady) — a stale closure here
    // would silently miscompute canon aggregates using long-ago watchlist state.
  }, [user, items, ratings, rewatchCount, shareActivity, statuses, progress, completedAt])

  // Day 30 of the guest lease (2026-10-15): the list goes READ-ONLY, never away. A mark
  // or an add past the lease opens the keep-this-list sheet instead of saving; the
  // library stays visible and comes back to life the moment an email is attached.
  const leaseUp = () => {
    if (!user?.is_anonymous || guestDaysLeft(user) > 0) return false
    window.dispatchEvent(new CustomEvent('nextep:toast', { detail: { title: 'Your 30 days on this phone are up', body: 'Add an email to keep marking. Nothing you added is lost.', key: 'lease' } }))
    window.dispatchEvent(new CustomEvent('nextep:open-save-ask', { detail: { src: 'lease' } }))
    return true
  }
  const saveProgress = useCallback(async (showId, prog, opts = {}) => {
    if (!user) { cbRef.current.onAuthRequired?.(); return }
    if (leaseUp()) return
    const prevProg = progress[showId] || null
    setProgress(prev => ({ ...prev, [showId]: prog }))
    const show = items.find(i => i.id === showId || i.id === +showId)
    // Callers that know exactly which episode was just watched pass it explicitly.
    // Fallback: derive from prog (prog is the NEXT episode to watch, so watched = prog - 1).
    // The fallback is only used for normal incremental marks; catch-up and finale
    // callers must pass watchedEp directly to avoid ghost episodes.
    // watchedEp: null means "don't log an episode activity" (e.g. rewatch start)
    // watchedEp: undefined uses the fallback formula
    const justWatchedEp = opts.watchedEp !== undefined ? opts.watchedEp : (prog.episode > 1
      ? { season: prog.season, episode: prog.episode - 1 }
      // prog.episode === 1 means the previous season's finale was just watched — not
      // "episode 0" of that season. Needs the previous season's actual episode count,
      // or there's no previous season (prog is S1E1 = nothing watched yet → null).
      : (prog.season > 1
          ? { season: prog.season - 1, episode: show?.seasons?.find(s => s.season_number === prog.season - 1)?.episode_count || 1 }
          : null))
    // A cascade mark ("mark watched through here") covers a whole RANGE, but only
    // ever passed watchedEp for the endpoint — every earlier episode in the range
    // got its progress-position moved past it, yet never got its own 'episode'
    // activity (feed, streak, stats all silently skipped it) or its own chance at
    // an Episode Pulse peek. opts.watchedRange carries the full span so those
    // earlier episodes are logged too, exactly like watching them one at a time
    // would (Jason 2026-09-09). The endpoint (justWatchedEp) is unaffected — this
    // only fills in {from..to-1}.
    //
    // Every episode this call actually covers, endpoint included — used for the
    // activities backfill AND (2026-09-14) to tell Episode Pulse how many episodes
    // just landed, so a real cascade gets its own multi-episode rating sheet
    // instead of every episode but the endpoint silently getting no rating
    // opportunity at all (Jason: "5 episodes can be marked but only one is asked
    // for a rating"). Computed BEFORE the endpoint's own writeActivity call below
    // so that call can carry the range summary in one push instead of firing N
    // separate "watched S·E" pushes to every follower (Jason, live: 6 pushes in
    // one minute for one sitting — Melissa cascade-marking a show).
    const rangeEps = []
    if (opts.watchedRange && justWatchedEp) {
      const { season: rSeason, from: rFrom } = opts.watchedRange
      for (let ep = rFrom; ep < justWatchedEp.episode; ep++) {
        if (rSeason === justWatchedEp.season && ep === justWatchedEp.episode) continue
        rangeEps.push({ season: rSeason, episode: ep })
      }
    }
    const allWatchedEps = [...rangeEps, ...(justWatchedEp ? [justWatchedEp] : [])]
    if (justWatchedEp) {
      // skipBroadcast on the range episodes (below), NOT here — the endpoint's own
      // call is the ONE push for the whole cascade, and carries fromEpisode/count
      // so notify-activity renders "watched S·E–E (N episodes)" instead of the
      // single-episode line. A normal single mark has rangeEps.length === 0, so
      // extra is just justWatchedEp, unchanged.
      const extra = rangeEps.length
        ? { ...justWatchedEp, fromEpisode: rangeEps[0].episode, count: allWatchedEps.length }
        : justWatchedEp
      writeActivity(user.id, shareActivity, 'episode', show, extra)
      // A real mark closes any open "watching now" session for this show — it
      // already resolved itself, no need to wait for the timed check-in to ask.
      endWatchSession(user.id, showId)
    }
    for (const ep of rangeEps) writeActivity(user.id, shareActivity, 'episode', show, ep, { skipBroadcast: true })
    // Funnel: the core loop. Bulk restores (imports, catch-up) stay silent like everything else here.
    // Props added 2026-09-12: 52 marks in 5 days by every active user, and the event
    // said nothing — not the show, not the episode, not which surface (show page
    // clapboard, Up Next button, queue swipe, Tonight lineup) did the marking. `src`
    // is the route, same convention as show_added.
    if (justWatchedEp && !opts.bulk) {
      // showId (the param) not show?.id (2026-09-14): `show` is looked up from
      // `items` by id and can miss if the watchlist hasn't finished syncing at the
      // moment of the mark — 9 of the first 99 logged marks came through with no
      // show at all, even though the actual mark and activity row were correct
      // (verified against `activities`, byte for byte). showId is the true
      // identifier already in hand; no lookup needed.
      logEvent('episode_marked', {
        show: showId, s: justWatchedEp.season, e: justWatchedEp.episode,
        src: opts.src || (typeof location !== 'undefined' ? location.pathname : '').replace(/^(\/show|\/aftershow|\/canon|\/curator|\/u)\/.*$/, '$1/*'),
      })
      try { window.dispatchEvent(new CustomEvent('nextep:episode_marked')) } catch {}
    }

    // Episode Pulse: the spoiler-safe post-watch beat. Announce the just-watched
    // episode so the global host can surface the community peek. Skip bulk imports
    // (they restore dozens of episodes and must stay silent, like every other
    // side-effect below). The host debounces, so a rapid catch-up run peeks once.
    if (justWatchedEp && !opts.bulk) {
      // `episodes`: every episode this call covered (2026-09-14) — length 1 for a
      // normal single mark (Pulse's existing peek/sheet flow, unchanged), length
      // >1 for a real cascade, which EpisodePulseHost routes to a multi-episode
      // rating sheet instead of asking about only the endpoint.
      const watchedDetail = {
        showId, showName: show?.name || '', poster: show?.poster_path || null,
        season: justWatchedEp.season, episode: justWatchedEp.episode,
        episodes: allWatchedEps,
      }
      // EpisodePulseHost is lazy; a mark before its chunk is up would announce to
      // nobody (audit 2026-10-31). Park it; the host replays it on mount if fresh.
      rememberWatched(watchedDetail)
      window.dispatchEvent(new CustomEvent('nextep:episode-watched', { detail: watchedDetail }))
    }

    // ── Season wrap, regardless of the door you walked through ──────────────
    // Crossing a season boundary earns the "That's a Wrap" clapboard no matter
    // WHERE the finale was marked (Calendar, My List, Home quick-mark, My Plans,
    // check-in) — previously only the ShowDetail path celebrated and every other
    // surface fell flat. ShowDetail passes opts.skipWrap (it orchestrates its own
    // richer wrap with race context), as does the add-show flow (its spotlight
    // moment already owns the screen).
    if (!opts.skipWrap && justWatchedEp && prog.season > justWatchedEp.season) {
      const doneSeason = show?.seasons?.find(s => s.season_number === justWatchedEp.season)
      window.dispatchEvent(new CustomEvent('nextep:wrap', { detail: {
        showId:       show?.id,
        poster:       show?.poster_path || null,   // the flood + the stamp row take the show's colour
        showName:     show?.name || '',
        type:         'season',
        season:       justWatchedEp.season,
        episodeCount: doneSeason?.episode_count,
        totalSeasons: (show?.seasons?.filter(s => s.season_number > 0).length) || undefined,
      }}))
    } else if (!opts.skipWrap && justWatchedEp && show) {
      // Caught up to the latest AIRED episode WITHOUT crossing a season boundary — the
      // streaming/day-and-date "season pause" finale. A Netflix season drops all at once,
      // so catching up = watching the finale, but there's no next season to advance INTO,
      // so the season-boundary wrap above never triggers. This mirrors ShowDetail's
      // `caughtUpMoment` EXACTLY so the clapboard fires from every mark surface (My List,
      // Home quick-mark, Calendar, check-in) the same way the post-watch card already does
      // — previously the caught-up cinematic lived ONLY in ShowDetail, so marking a Netflix
      // finale anywhere else gave the rating/Pulse/character card but no cinematic
      // (Jason 2026-08-10). ShowDetail passes skipWrap and keeps its richer version, so the
      // !opts.skipWrap guard prevents a double-fire; the else keeps it off the mid-series
      // case that already works above.
      const effLast = effectiveLastAired(show, todayKey())
      const hasDatedNext = !!show.next_episode_to_air?.air_date
      if (!isCaughtUp(prevProg, effLast) && isCaughtUp(prog, effLast)
          && !isEndedStatus(show.status) && statuses[showId] !== 'completed' && !hasDatedNext) {
        window.dispatchEvent(new CustomEvent('nextep:wrap', { detail: {
          showId: show.id, poster: show.poster_path, showName: show.name || '',
          type: 'caughtup', season: effLast?.season, episodeCount: show.number_of_episodes,
        }}))
      }
    }

    // ── In-sync detection ────────────────────────────────────────────────────
    // After saving progress, check if any followed friend is at the exact same
    // next-up episode. If yes, fire a small celebration + write an activity row.
    // De-duped per (show, friend, episode) per session.
    // Bulk (import) marks skip every celebration/activity/streak/binge side-effect
    // below — a TV Time import restores dozens of shows and must stay silent.
    if (!opts.bulk && justWatchedEp) try {   // pointer-only corrections (watchedEp:null) must not claim "in sync"
      const friendsHere = getFriendsOnShow(user.id, showId)
      const match = friendsHere.find(f =>
        f.progress?.season === prog.season && f.progress?.episode === prog.episode
      )
      if (match && !hasFiredInSync(showId, match.id, prog.season, prog.episode)) {
        markFiredInSync(showId, match.id, prog.season, prog.episode)
        const friendName = match.display_name || match.username
        window.dispatchEvent(new CustomEvent('nextep:milestone', { detail: {
          title: `In sync with ${friendName}! ◉`,
          body:  `You're both on S${prog.season}·E${prog.episode} of ${show?.name || 'this show'}`,
          icon:  '◉',
          route: match.username ? `/u/${match.username}` : `/show/${showId}`,
        }}))
        writeActivity(user.id, shareActivity, 'insync', show, {
          with_username: match.username,
          with_name:     friendName,
          season:        prog.season,
          episode:       prog.episode,
        })
      }
    } catch (err) { /* never block save on celebration */ }
    // Track date for streak calculation — and celebrate the moment it's kept alive.
    if (!opts.bulk) try {
      const today = todayKey()
      const raw   = localStorage.getItem('nextep_activity_dates')
      const dates = raw ? JSON.parse(raw) : []
      if (!dates.includes(today)) {
        const nextDates = recordActivityToday(today)
        // This is the FIRST watch of the day, so it just took the streak from "at risk" to
        // "safe" — the celebration Jason asked for. Guarded to fire at most once/day (the
        // includes check above), only when an episode was actually watched (justWatchedEp,
        // not an un-mark/correction), and only when there's a real run to protect (>= 2),
        // so it reads as "you saved it," not noise. streakFromDays is pure — no DB call in
        // the mark hot path.
        if (justWatchedEp) {
          const { current } = streakFromDays(new Set(nextDates), today)
          if (current >= 2) {
            // Round-number days get the full-screen cinematic instead of the toast
            // (same no-double-surface rule as achievement-unlocked) — every other
            // day of the streak keeps the lightweight reassurance toast.
            if (isWatchStreakMilestone(current)) {
              window.dispatchEvent(new CustomEvent('nextep:streak-milestone', { detail: {
                title: `${current}-Day Streak`,
                body: current >= 365 ? 'A full year of daily watching. Legendary.'
                  : current >= 100 ? `${current} days running, unstoppable.`
                  : current >= 30  ? `${current} days straight, a month of perfect attendance.`
                  :                  `${current} days in a row, you kept the fire lit.`,
                icon: '🔥',
                route: '/profile?tab=stats',
              }}))
            } else {
              window.dispatchEvent(new CustomEvent('nextep:milestone', { detail: {
                title: `🔥 ${current}-day streak saved!`,
                body: 'Nice, you kept the fire lit. See you tomorrow to keep it going.',
                icon: '🔥',
                route: '/profile?tab=stats',
              }}))
            }
          }
        }
      }
    } catch {}
    // Binge mode detection
    if (!opts.bulk) {
      bingeCountRef.current += 1
      clearTimeout(bingeTimerRef.current)
      bingeTimerRef.current = setTimeout(() => { bingeCountRef.current = 0 }, 30 * 60 * 1000) // reset after 30min
      if (bingeCountRef.current === 3) {
        cbRef.current.onBinge?.(show)
        writeActivity(user.id, shareActivity, 'binge', show, {})
      }
    }
    // Auto-upgrade status: if user is marking episodes but status is still
    // plan_to_watch, silently promote to watching. This prevents the drift
    // where a show has S14·E3 progress but still says "Plan to Watch".
    const currentStatus = statuses[showId]
    // A mark on a finished show whose new season has started is the resume itself
    // (the deck hands it over at the new season's E1); the finish date is kept.
    const shouldUpgrade = currentStatus === 'plan_to_watch' || (currentStatus === 'completed' && justWatchedEp)
    if (shouldUpgrade) {
      setStatuses(prev => ({ ...prev, [showId]: 'watching' }))
    }
    // ── Will this mark complete the show? ───────────────────────────────────
    // If so, setStatus('completed') is about to write the derived final-episode
    // progress. Writing `prog` here too means TWO near-simultaneous updates to the
    // same row with potentially different progress → non-deterministic last-write
    // wins (flip-flop when episode_count includes specials). So when completion is
    // imminent, let setStatus own the single progress write (Jason 2026-06-29).
    const notYetComplete = statuses[showId] !== 'completed'
    const isEndedShow = isEndedStatus(show?.status)
    // Route finale detection through deriveFinalEpisode (shared) instead of the raw
    // last_episode_to_air — so an ended show whose cached data lacks that field (a TMDB
    // gap) still auto-completes off its true finale (its fallbacks: highest real season's
    // episode_count, then the count fields). Gated by isEndedShow, so returning shows are
    // untouched — the fallbacks only ever apply to a show that's actually over.
    const _finale = deriveFinalEpisode(show)
    const isFinaleMark = !!(justWatchedEp && _finale &&
      justWatchedEp.season === _finale.season &&
      justWatchedEp.episode >= _finale.episode)
    const autoCompleteHere = !opts.skipWrap && isEndedShow && isFinaleMark && notYetComplete
    // ShowDetail completion paths (the wide "Mark Complete" / ended-finale mark)
    // flag themselves and call setStatus directly — EXCEPT opts.seriesFinale needs
    // `_finale` truthy to trust that. ShowDetail's own completion guard also
    // requires deriveFinalEpisode(show) non-null, so when TMDB data is missing it
    // (an ended show with no last_episode_to_air, no season episode_counts, no
    // count fields) ShowDetail sets seriesFinale:true but never calls setStatus —
    // and if this write were skipped too, the mark would silently revert on
    // reload (data-loss bug, audit 2026-08-16). Only skip the write when there's
    // real finale data backing the promise that setStatus will own it.
    const completionImminent = notYetComplete && (autoCompleteHere || opts.userComplete || (opts.seriesFinale && _finale))

    // Persist to DB (skip the progress write when setStatus will own it).
    // updated_at is stamped so marking an episode counts as daily-streak activity — the
    // streak reads only watchlist.updated_at and there's no DB trigger, so a bare
    // { progress } update left the timestamp (and the streak) frozen (the "watched 5 and
    // the streak still says at-risk" bug). App-driven reconcile writes use a separate
    // direct update and deliberately DON'T stamp it, so opening the app can't fake a day.
    if (!completionImminent) {
      const stamp = new Date().toISOString()
      const dbUpdate = shouldUpgrade ? { progress: prog, status: 'watching', updated_at: stamp } : { progress: prog, updated_at: stamp }
      // Not awaited (the gesture must not wait on the radio), but a refusal — RLS, a
      // row removed on another device, an expired session — puts the card back
      // instead of leaving a mark on screen that never happened.
      persistRow(user.id, showId, dbUpdate, 'progress').then(res => {
        if (res !== 'failed') return
        setProgress(prev => prevProg ? { ...prev, [showId]: prevProg } : (({ [showId]: _, ...rest }) => rest)(prev))
        if (shouldUpgrade) setStatuses(prev => ({ ...prev, [showId]: currentStatus || 'plan_to_watch' }))
      })
    }

    // ── Undo affordance ──────────────────────────────────────────────────────
    // Marks commit optimistically; the global UndoToast offers a few seconds to
    // revert a fat-fingered tap.
    //
    // Undo used to restore progress and NOTHING ELSE, writing the revert straight to
    // the row. That skipped the backward-correction path a few lines below — the one
    // that reopens confirmed plans — so an undone episode stayed `confirmed` and never
    // came back to Tonight, while the marking side had ALSO auto-confirmed its plan and
    // written a public "watched" activity. Jason hit exactly this: fat-fingered
    // Hell's Kitchen S24·E11 mid-run, tapped Undo, and got a correct next-up with the
    // episode missing from Tonight and a Pulse prompt for an episode he hadn't watched
    // (2026-07-30). Undo now reverses the same three things marking caused.
    //
    // What it still deliberately does NOT touch: watchlist.updated_at, i.e. the streak
    // date. That was the original intent of "not history surgery" and it stands — you
    // did open the app and watch something today. Retracting the FALSE CLAIM about a
    // specific episode is a different thing from rewriting your streak.
    if (justWatchedEp) {
      window.dispatchEvent(new CustomEvent('nextep:progress-marked', { detail: {
        showId,
        showName: show?.name || '',
        watchedEp: justWatchedEp,
        // rangeEps + the endpoint itself = the real count for a cascade mark, so
        // UndoToast (the one confirmation every mark-watched surface already shares)
        // can say "5 episodes" instead of just the endpoint — see below, this is what
        // let per-page callers drop their own redundant "N episodes watched" toast,
        // which used to double up with this one in the same corner of the screen
        // (Jason 2026-09-17: "clapboard, rating... mixed together").
        count: rangeEps.length + 1,
        undo: () => {
          setProgress(prev => prevProg ? { ...prev, [showId]: prevProg } : (({ [showId]: _, ...rest }) => rest)(prev))
          // Restore the PRE-mark status, not just the shouldUpgrade case: this same
          // mark may have auto-completed the show (autoCompleteHere → setStatus below)
          // or flipped watching→caught_up inline — undo must reverse both, or a
          // fat-fingered finale leaves status=completed with mid-season progress
          // and a completed_at stamp. The minted card stays (mint is idempotent;
          // re-completing later won't duplicate it), but the status, completion
          // date, and the public claim all revert.
          const statusBefore = shouldUpgrade ? (currentStatus || 'plan_to_watch') : (statuses[showId] || null)
          const completedAtBefore = completedAt[showId] || null
          if (statusBefore) setStatuses(prev => ({ ...prev, [showId]: statusBefore }))
          setCompletedAt(prev => {
            if ((prev[showId] || null) === completedAtBefore) return prev
            return completedAtBefore ? { ...prev, [showId]: completedAtBefore } : (({ [showId]: _, ...rest }) => rest)(prev)
          })
          const revert = { progress: prevProg, completed_at: completedAtBefore }
          if (statusBefore) revert.status = statusBefore
          persistRow(user.id, showId, revert, 'undo')

          // 1. Reopen the plan this mark auto-confirmed, so the episode returns to
          //    Tonight. Mirrors the movedBackward branch below (same filter shape).
          supabase.from('watch_plans')
            .update({ status: 'pending', confirmed_at: null })
            .eq('user_id', user.id).eq('show_id', showId).eq('status', 'confirmed')
            .or(`season.gt.${justWatchedEp.season},and(season.eq.${justWatchedEp.season},episode.gte.${justWatchedEp.episode})`)
            .then(({ error }) => {
              if (!error) window.dispatchEvent(new CustomEvent('nextep:plan_reopened', { detail: { showId } }))
            })

          // 2. Retract the public claim. The `episode` activity says you watched this
          //    on your feed and your followers'; leaving it after an undo publishes
          //    something untrue. Scoped hard: this show, this exact episode, only the
          //    rows this mark just created (5 min), only the two claim types.
          const since = new Date(Date.now() - 5 * 60 * 1000).toISOString()
          supabase.from('activities').delete()
            .eq('user_id', user.id).eq('show_id', showId)
            .in('type', ['episode', 'plan_confirmed'])
            .eq('data->>season', String(justWatchedEp.season))
            .in('data->>episode', allWatchedEps.map(e => String(e.episode)))
            .gte('created_at', since)
            .then(() => {})
          // If this mark auto-completed the show, a 'completed' activity went out
          // too (it carries no season/episode keys, so the delete above misses it).
          supabase.from('activities').delete()
            .eq('user_id', user.id).eq('show_id', showId)
            .eq('type', 'completed')
            .gte('created_at', since)
            .then(() => {})

          // 3. Call off the Episode Pulse prompt. Its host debounces the watch event by
          //    900ms, so an undo inside that window should cancel it outright; a slower
          //    undo dismisses the peek that already landed.
          window.dispatchEvent(new CustomEvent('nextep:episode-unwatched', { detail: {
            showId, season: justWatchedEp.season, episode: justWatchedEp.episode, episodes: allWatchedEps,
          }}))
        },
      }}))
    }

    // Un-mark / correction (the ↺ "set as next up" button, or any backward jump):
    // reopen confirmed plans at-or-after the new next-up episode so an episode you
    // un-watch returns to Tonight's Lineup instead of staying stuck 'confirmed'
    // forever — the asymmetry with auto-confirm-on-watch (Jason 2026-06-29).
    const movedBackward = prevProg && (prog.season < prevProg.season ||
      (prog.season === prevProg.season && prog.episode < prevProg.episode))
    if (movedBackward) {
      // Only plans confirmed in the last 24h: this exists to undo a fresh mis-mark,
      // not to resurrect an old run. A reposition/rewatch weeks later used to flip
      // every historical confirmed plan back to pending, which then rolled onto
      // Tonight indefinitely (the Punisher ×11 lineup flood, 2026-09-05).
      const recent = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      supabase.from('watch_plans')
        .update({ status: 'pending', confirmed_at: null })
        .eq('user_id', user.id)
        .eq('show_id', showId)
        .eq('status', 'confirmed')
        .gte('confirmed_at', recent)
        .or(`season.gt.${prog.season},and(season.eq.${prog.season},episode.gte.${prog.episode})`)
        .then(({ error }) => {
          if (!error) window.dispatchEvent(new CustomEvent('nextep:plan_reopened', { detail: { showId } }))
        })
    }

    if (!justWatchedEp && !movedBackward && !opts.bulk) {
      supabase.from('watch_plans')
        .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('show_id', showId)
        .eq('status', 'pending')
        .or(`season.lt.${prog.season},and(season.eq.${prog.season},episode.lt.${prog.episode})`)
        .then(({ error }) => {
          if (!error) window.dispatchEvent(new CustomEvent('nextep:plan_reopened', { detail: { showId } }))
        })
    }

    // Auto-confirm matching watch_plan (Tonight Mode) — if user planned to watch this
    // exact episode tonight and just marked it watched, fold the plan automatically
    // so it disappears from /tonight without requiring the check-in modal.
    if (justWatchedEp) {
      // Confirm the plan for this episode AND any earlier ones — a cascade-mark
      // (or catch-up jump) covers everything at-or-before the watched ep, and
      // those intermediate plans previously lingered pending until the next-day
      // sweep.
      supabase.from('watch_plans')
        .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('show_id', showId)
        .or(`season.lt.${justWatchedEp.season},and(season.eq.${justWatchedEp.season},episode.lte.${justWatchedEp.episode})`)
        .eq('status', 'pending')
        .then(({ error }) => {
          if (!error) {
            window.dispatchEvent(new CustomEvent('nextep:plan_confirmed_via_episode', {
              detail: { showId, season: justWatchedEp.season, episode: justWatchedEp.episode }
            }))
          }
        })
    }

    // ── Series finale from ANY surface → the full completion ceremony ───────
    // If the episode just watched is the true final episode of a confirmed-ended
    // show, complete it via setStatus (which owns FinalCut + card + milestone).
    // Previously only the ShowDetail path auto-completed — finishing a series
    // from a My List row or the Calendar left status=watching and the biggest
    // moment in the app silently never fired. skipWrap (ShowDetail / add-flow)
    // means the caller orchestrates completions itself.
    if (autoCompleteHere) {
      setStatus(showId, 'completed')
    } else if (!completionImminent) {
      // Lean-surface caught_up reconcile: marking a returning show's latest aired
      // episode from Home/MyList/Calendar (not the show page) used to leave status
      // 'watching' until the next app-load boot sweep ("the LIST lies Watching"). Flip
      // inline via the SAME shared decision so it's honest immediately. Ended/rewatching
      // → null (skipped); handles both directions if a correction un-catches-you-up.
      const effStatus = shouldUpgrade ? 'watching' : statuses[showId]
      const flip = caughtUpFlip(show, prog, effStatus, todayKey())
      if (flip && flip !== effStatus) {
        setStatuses(prev => ({ ...prev, [showId]: flip }))
        supabase.from('watchlist').update({ status: flip }).eq('user_id', user.id).eq('show_id', showId).then(() => {}, () => {})
      }
    }
  }, [user, items, statuses, progress, completedAt, shareActivity, setStatus])

  // Silent plan_to_watch → watching promotion, for actions that signal intent to watch
  // WITHOUT a watched episode — chiefly planning an episode for Tonight. Mirrors the
  // auto-upgrade saveProgress already does on a real mark, so a show you've queued up
  // stops reading "Plan to Watch." Only touches plan_to_watch rows (no-op otherwise, so
  // callers can fire it blindly). Deliberately status-ONLY: no 'started watching' activity
  // broadcast (you haven't watched yet) and no updated_at bump (planning isn't a watch, so
  // it must not inflate the daily streak). Reverts local state on a failed write.
  const promoteFromPlan = useCallback(async (showId) => {
    if (!user || statuses[showId] !== 'plan_to_watch') return
    setStatuses(prev => ({ ...prev, [showId]: 'watching' }))
    const { error } = await supabase.from('watchlist')
      .update({ status: 'watching' }).eq('user_id', user.id).eq('show_id', showId)
    if (error) setStatuses(prev => ({ ...prev, [showId]: 'plan_to_watch' }))
  }, [user, statuses])

  // O(1) lookup set — rebuilt only when items change (avoids O(n) scan on every render)
  const itemIdSet = useMemo(() => new Set(items.flatMap(i => i?.id ? [i.id, +i.id] : [])), [items])
  const has = useCallback((id) => itemIdSet.has(id) || itemIdSet.has(+id), [itemIdSet])

  // Update cached show_data in watchlist when fresh TMDB data is available
  // Call from ShowDetail after fetching — keeps "Aired This Week" and streaks accurate
  const refreshShowData = useCallback(async (showId, freshData) => {
    if (!user || !freshData?.id) return
    const existingItem = items.find(i => i?.id === showId || i?.id === +showId)
    if (!existingItem) return
    // Only update if key fields have changed
    const needsUpdate = 
      freshData.last_episode_to_air?.episode_number !== existingItem.last_episode_to_air?.episode_number ||
      freshData.next_episode_to_air?.air_date !== existingItem.next_episode_to_air?.air_date ||
      freshData.status !== existingItem.status
    if (!needsUpdate) return
    const merged = { ...existingItem, ...freshData }
    setItems(prev => prev.map(i => (i?.id === showId || i?.id === +showId) ? merged : i))
    // Persist update to DB in background
    supabase.from('watchlist').update({ show_data: merged })
      .eq('user_id', user.id).eq('show_id', showId).catch(() => {})
  }, [user, items])

  // Memoize the returned object so its IDENTITY only changes when real data/callbacks
  // change — NOT on every App re-render. `wl` is passed to ~35 components; an unstable
  // literal here defeated every downstream React.memo and re-rendered nearly the whole
  // app on any unrelated App state flip (a toast, a modal, an unseen-count tick). All 9
  // callbacks are already useCallback-stable, so this wrapper is the only leak. Deps list
  // EVERY referenced value — miss one and you ship a stale-closure bug.
  return useMemo(
    () => ({ userId: user?.id, shareActivity, items, ratings, notes, epNotes, statuses, progress, rewatchCount, completedAt, finaleRatings, ready, add, remove, rate, rateFinale, saveNote, saveEpNote, setStatus, saveProgress, promoteFromPlan, has, refreshShowData }),
    [user?.id, shareActivity, items, ratings, notes, epNotes, statuses, progress, rewatchCount, completedAt, finaleRatings, ready, add, remove, rate, rateFinale, saveNote, saveEpNote, setStatus, saveProgress, promoteFromPlan, has, refreshShowData]
  )
}
